import { Component, Input, ViewChild, OnChanges, OnInit } from '@angular/core';
import { ScoreInfoType } from '../../constants-types/Enums';
import { BehavioralFactorRanges } from '../../core-data-model/BehavioralFactorRanges.model';
import { Pattern, FullPattern } from '../../core-data-model/Pattern.model';
import { SVGUtilities } from '../../core-utilities/SVG.utility';
import { Util } from '../../core-utilities/Util';
import { TranslationService } from '../../translations';
import { BehavioralPatternImageComponent } from './BehavioralPatternImage.component';

export interface BehavioralPatternGraphicOptions {
    showScoreId: boolean;
    showCopyright: boolean;
    showTitle: boolean;
}

@Component({
    selector: 'behavioral-pattern',
    template: `
        @if (!!behavioralPattern) {
            @if (behavioralPattern.customTitle) {
                <div
                    class="mediumVerticalSpace veryLargeText event-coachWithSelfConcept-OutcomeMeasure"
                >
                    {{ behavioralPattern.customTitle.personName }} :
                    {{
                        'personDashboard-behavioralAssessments.coachWithSelfConcept'
                            | translate
                    }}
                </div>
            }
            <div
                class="scalingSvgContainer"
                [ngStyle]="{ 'padding-bottom': paddingBottom }"
            >
                <svg
                    class="entireImage scalingSvg"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink"
                    [attr.viewBox]="viewBox"
                >
                    <svg:defs>
                        <svg:linearGradient
                            id="linearGradient"
                            x1="0%"
                            y1="0%"
                            x2="200%"
                            y2="0%"
                        >
                            <svg:stop offset="0%" stop-color="#b6e4fa" />
                            <svg:stop offset="100%" stop-color="#0092c8" />
                        </svg:linearGradient>
                        <svg:path
                            id="letterE"
                            d="M4.47,1.72h-6.57v4.45h7.71v2.75H-5.52V-7.7h11.11v2.77h-7.69v4h6.57Z"
                            style="fill:#0092c8"
                        />
                    </svg:defs>
                    <style>
                        text {
                            font-family: Roboto-Regular, Roboto;
                        }
                        .entireImage {
                            background: white;
                        }
                        .highlightA,
                        .highlightB,
                        .highlightC,
                        .highlightD,
                        .highlightE {
                            display: none;
                            opacity: 0;
                            transition: opacity 0.3s;
                        }
                        .highlight--active,
                        .highlightA:hover,
                        .highlightB:hover,
                        .highlightC:hover,
                        .highlightD:hover,
                        .highlightE:hover {
                            opacity: 1;
                            display: block;
                        }
                        .circleA:hover ~ .highlightA,
                        .circleB:hover ~ .highlightB,
                        .circleC:hover ~ .highlightC,
                        .circleD:hover ~ .highlightD,
                        .circleE:hover ~ .highlightE {
                            opacity: 1;
                            display: block;
                        }
                    </style>
                    <svg:g>
                        @if (!!behavioralPattern) {
                            <ng-container>
                                @if (jobRanges) {
                                    <svg:g
                                        [behavioralRangesRectangles]="jobRanges"
                                        [attr.transform]="
                                            SVG.outputTranslate(
                                                0,
                                                viewAll
                                                    ? behavioralPattern.customTitle
                                                        ? 53
                                                        : 27
                                                    : -5
                                            )
                                        "
                                    ></svg:g>
                                }
                                @if (behavioralPattern?.customTitle?.self) {
                                    <svg:text
                                        transform="translate(0, 6)"
                                        style="font-weight:400;font-size:20px"
                                    >
                                        {{ behavioralPattern.customTitle.self }}
                                    </svg:text>
                                }
                                <svg:g
                                    #self
                                    [attr.transform]="
                                        SVG.outputTranslate(
                                            0,
                                            behavioralPattern?.customTitle
                                                ? 20
                                                : -6
                                        )
                                    "
                                    [singleBehavioralPattern]="
                                        behavioralPattern.self
                                    "
                                    [options]="getOptions(ScoreInfoType.Self)"
                                    [hoveredFactor]="hoveredFactor"
                                    [showE]="false"
                                    [languageLocale]="languageLocale"
                                ></svg:g>
                                @if (viewAll && behavioralPattern.selfConcept) {
                                    <ng-container>
                                        @if (
                                            behavioralPattern?.customTitle
                                                ?.selfConcept
                                        ) {
                                            <svg:text
                                                y="355"
                                                style="font-weight:400;font-size:20px"
                                            >
                                                {{
                                                    behavioralPattern
                                                        .customTitle.selfConcept
                                                }}
                                            </svg:text>
                                        }
                                        <svg:g
                                            #selfConcept
                                            [singleBehavioralPattern]="
                                                behavioralPattern.selfConcept
                                            "
                                            [options]="
                                                getOptions(
                                                    ScoreInfoType.SelfConcept
                                                )
                                            "
                                            [attr.transform]="
                                                SVG.outputTranslate(
                                                    0,
                                                    offset([
                                                        behavioralPattern.self
                                                    ])
                                                )
                                            "
                                            [showE]="false"
                                            [languageLocale]="languageLocale"
                                        ></svg:g>
                                    </ng-container>
                                }
                                @if (viewAll && behavioralPattern.synthesis) {
                                    <ng-container>
                                        @if (
                                            behavioralPattern?.customTitle
                                                ?.synthesis
                                        ) {
                                            <svg:text
                                                y="730"
                                                style="font-weight:400;font-size:20px"
                                            >
                                                {{
                                                    behavioralPattern
                                                        .customTitle.synthesis
                                                }}
                                            </svg:text>
                                        }
                                        <svg:g
                                            #synthesis
                                            [singleBehavioralPattern]="
                                                behavioralPattern.synthesis
                                            "
                                            [options]="
                                                getOptions(
                                                    ScoreInfoType.Synthesis
                                                )
                                            "
                                            [scoreId]="
                                                behavioralPattern.scoreId
                                            "
                                            [attr.transform]="
                                                SVG.outputTranslate(
                                                    0,
                                                    offset([
                                                        behavioralPattern.self,
                                                        behavioralPattern.selfConcept
                                                    ])
                                                )
                                            "
                                            [showE]="true"
                                            [languageLocale]="languageLocale"
                                        ></svg:g>
                                    </ng-container>
                                }
                            </ng-container>
                        }
                    </svg:g>
                </svg>
            </div>
        }
    `,
    standalone: false
})
export class BehavioralPatternImageWrapperComponent
    implements OnChanges, OnInit
{
    spacingBetweenPatterns = 20;

    paddingAmount = 0;
    width = 1100;

    @Input()
    behavioralPattern: FullPattern;
    @Input()
    options: BehavioralPatternGraphicOptions;
    @Input()
    hoveredFactor: string;
    @Input()
    viewAll: boolean;
    @Input()
    jobRanges: BehavioralFactorRanges;

    totalHeight: number;
    viewBox: string;
    paddingBottom: string;

    realOptions: BehavioralPatternGraphicOptions = {
        showScoreId: true,
        showCopyright: false,
        showTitle: true
    };
    @Input()
    languageLocale: string = null;

    get propertiesList(): string[] {
        return ['behavioralPattern', 'options', 'viewAll'];
    }

    SVG: SVGUtilities;

    @ViewChild('self', { static: false })
    self: BehavioralPatternImageComponent;
    @ViewChild('selfConcept', { static: false })
    selfConcept: BehavioralPatternImageComponent;
    @ViewChild('synthesis', { static: false })
    synthesis: BehavioralPatternImageComponent;

    // Expose Enum to Template
    ScoreInfoType = ScoreInfoType;

    constructor(
        public TR: TranslationService,
        util: Util
    ) {
        this.SVG = util.svg;
    }
    ngOnInit() {
        if (this.behavioralPattern.customTitle) {
            this.spacingBetweenPatterns = 85;
        }

        this.totalHeight = this.calculateTotalHeight();
        this.viewBox = this.getViewbox();
        this.paddingBottom = this.getPaddingBottom();
    }

    ngOnChanges() {
        // TODO - this couild be more programmatic - it is overly coupled now
        if (this.options) {
            this.realOptions.showCopyright = this.options.showCopyright;
            this.realOptions.showScoreId = this.options.showScoreId;
            this.realOptions.showTitle = this.options.showTitle;
        }

        this.totalHeight = this.calculateTotalHeight();
        this.viewBox = this.getViewbox();
        this.paddingBottom = this.getPaddingBottom();
    }

    getViewbox(): string {
        return `0 0 ${this.width} ${this.calculateTotalHeight()}`;
    }

    getPaddingBottom(): string {
        return this.SVG.getPaddingBottom(
            this.width,
            this.calculateTotalHeight()
        );
    }

    isFullPattern(): boolean {
        return (
            !!this.behavioralPattern.selfConcept &&
            !!this.behavioralPattern.synthesis
        );
    }

    // TODO - this is a stub method that need sot be rewritten
    offset(arrayOfPrevious: Pattern[]) {
        return arrayOfPrevious.reduce((accumulator, pattern) => {
            if (!pattern) {
                return 0;
            }

            return (
                accumulator +
                this.getPatternHeight(pattern) +
                this.spacingBetweenPatterns
            );
        }, 0);
    }

    getPatternHeight(pattern: Pattern): number {
        let height = 240;
        if (this.getOptions(pattern.type).showTitle) {
            height += 40;
        }
        if (pattern.E !== undefined && pattern.E !== null) {
            height += 50;
        }
        return height;
    }

    calculateTotalHeight() {
        if (this.behavioralPattern) {
            if (this.isFullPattern() && this.viewAll) {
                const usedHeight = Math.max(
                    0,
                    this.offset([
                        this.behavioralPattern.self,
                        this.behavioralPattern.selfConcept,
                        this.behavioralPattern.synthesis
                    ]) -
                        this.spacingBetweenPatterns +
                        this.paddingAmount
                );
                return usedHeight;
            }
            return (
                this.getPatternHeight(this.behavioralPattern.self) +
                this.paddingAmount
            );
        }
        return 0;
    }
    getOptions(type: ScoreInfoType): BehavioralPatternGraphicOptions {
        const options = {
            showScoreId: false,
            showCopyright: false,
            showTitle: this.realOptions.showTitle
        } as BehavioralPatternGraphicOptions;

        if (type == ScoreInfoType.Synthesis) {
            options.showScoreId = this.realOptions.showScoreId;
            options.showCopyright = this.realOptions.showCopyright;
        }

        if (this.isFullPattern() && type === ScoreInfoType.Self) {
            options.showTitle = this.viewAll;
        }

        return options;
    }
}
